<template>
  <div>
    <FormInputWrapper label="Preferred travel dates and schedule" :error="props.error" :required="true">
      <!-- 選択状態の表示 -->
      <div class="mb-2 flex items-center gap-2 font-Spectral text-sm">
        <template v-if="startDate && endDate">
          <span class="text-gray9">{{ displayStartDate }} — {{ displayEndDate }}</span>
          <button class="text-gold3 underline hover:text-gold1" type="button" @click="clearSelection">Clear</button>
        </template>
        <template v-else-if="startDate">
          <span class="text-gray9">{{ displayStartDate }} —</span>
          <span class="text-gray2">Please select an end date</span>
          <button class="text-gold3 underline hover:text-gold1" type="button" @click="clearSelection">Clear</button>
        </template>
        <span v-else class="text-gray2">Please select a start date</span>
      </div>

      <!-- カレンダー -->
      <MoleculesWabunkaCalendar class="!bg-white" lang="en" :is-error="!!props.error">
        <template #content="{ day: calendarDay, isCurrentMonth }">
          <ItineraryDurationPickerDay
            :day="calendarDay"
            :end-date="endDate"
            :is-current-month="isCurrentMonth"
            :max-date="maxDate"
            :min-date="minDate"
            :on-click="onDayClick"
            :selection-phase="selectionPhase"
            :start-date="startDate"
          />
        </template>
      </MoleculesWabunkaCalendar>
    </FormInputWrapper>
  </div>
</template>

<script setup lang="ts">
import MoleculesWabunkaCalendar from '@/components/molecules/wabunka/Calendar/Calendar.vue';
import { getCurrentJstDayjs, formatDurationRangeEn } from '@/utils/date';
import FormInputWrapper from '@/wabunka-src/common/components/pure/FormInputWrapper/FormInputWrapper.vue';
import ItineraryDurationPickerDay from '@/wabunka-src/features/Itinerary/pages/Itinerary/components/ItineraryDurationPicker/ItineraryDurationPickerDay.vue';

import type dayjs from 'dayjs';

interface Props {
  modelValue: string;
  error?: string;
  onUpdateModelValue?: (value: string) => void;
}

const props = withDefaults(defineProps<Props>(), {
  error: '',
  onUpdateModelValue: undefined,
});

// 選択状態管理
const startDate = ref<dayjs.Dayjs | null>(null);
const endDate = ref<dayjs.Dayjs | null>(null);
/** 'start': 開始日を選択中, 'end': 終了日を選択中 */
const selectionPhase = ref<'start' | 'end'>('start');

// 制約定数
const MAX_MONTHS_AHEAD = 24;
const DAYS_UNTIL_SELECTABLE = 3;
const today = computed(() => getCurrentJstDayjs().startOf('day'));
const minDate = computed(() => today.value.add(DAYS_UNTIL_SELECTABLE, 'day'));
const maxDate = computed(() => today.value.add(MAX_MONTHS_AHEAD, 'month'));

/** 選択範囲の表示用 */
const displayStartDate = computed(() => (startDate.value ? startDate.value.locale('en').format('MMM D, YYYY') : ''));
const displayEndDate = computed(() => (endDate.value ? endDate.value.locale('en').format('MMM D, YYYY') : ''));

/** 子コンポーネントからの日付クリック */
const onDayClick = (day: dayjs.Dayjs) => {
  if (selectionPhase.value === 'start') {
    startDate.value = day;
    endDate.value = null;
    selectionPhase.value = 'end';
    props.onUpdateModelValue?.('');
  } else if (startDate.value && day.isAfter(startDate.value, 'day')) {
    endDate.value = day;
    selectionPhase.value = 'start';
    props.onUpdateModelValue?.(formatDurationRangeEn(startDate.value, day));
  }
};

const clearSelection = () => {
  startDate.value = null;
  endDate.value = null;
  selectionPhase.value = 'start';
  props.onUpdateModelValue?.('');
};
</script>
